"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.Keyring = void 0;

var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));

var _objectSpread2 = _interopRequireDefault(require("@babel/runtime/helpers/objectSpread"));

var _store = _interopRequireDefault(require("store"));

var _pair = _interopRequireDefault(require("@polkadot/keyring/pair"));

var _util = require("@polkadot/util");

var _development = _interopRequireDefault(require("./observable/development"));

var _Base = _interopRequireDefault(require("./Base"));

var _defaults = require("./defaults");

var _options = _interopRequireDefault(require("./options"));

// Copyright 2017-2019 @polkadot/ui-keyring authors & contributors
// This software may be modified and distributed under the terms
// of the Apache-2.0 license. See the LICENSE file for details.
const RECENT_EXPIRY = 24 * 60 * 60; // No accounts (or test accounts) should be loaded until after the chain determination.
// Chain determination occurs outside of Keyring. Loading `keyring.loadAll({ type: 'ed25519' | 'sr25519' })` is triggered
// from the API after the chain is received

class Keyring extends _Base.default {
  addExternal(publicKey) {
    let meta = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const pair = this.keyring.addFromAddress(publicKey, (0, _objectSpread2.default)({}, meta, {
      isExternal: true
    }), null);
    const json = this.saveAccount(pair);
    return {
      json,
      pair
    };
  }

  addPair(pair, password) {
    this.keyring.addPair(pair);
    const json = this.saveAccount(pair, password);
    return {
      json,
      pair
    };
  }

  addUri(suri, password) {
    let meta = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    let type = arguments.length > 3 ? arguments[3] : undefined;
    const pair = this.keyring.addFromUri(suri, meta, type);
    const json = this.saveAccount(pair, password);
    return {
      json,
      pair
    };
  }

  backupAccount(pair, password) {
    if (!pair.isLocked()) {
      pair.lock();
    }

    pair.decodePkcs8(password);
    return pair.toJson(password);
  }

  createAccount(seed, password, meta) {
    console.warn('createAccount deprecated, use addUri instead');
    return this.addUri((0, _util.u8aToHex)(seed), password, meta).pair;
  }

  createAccountExternal(publicKey, meta) {
    console.warn('createAccountExternal deprecated, use addExternal instead');
    return this.addExternal(publicKey, meta).pair;
  }

  createAccountMnemonic(seed, password, meta) {
    console.warn('createAccountMnemonic deprecated, use createUri instead');
    return this.addUri(seed, password, meta).pair;
  }

  createFromUri(suri) {
    let meta = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let type = arguments.length > 2 ? arguments[2] : undefined;
    return this.keyring.createFromUri(suri, meta, type);
  }

  encryptAccount(pair, password) {
    const json = pair.toJson(password);
    json.meta.whenEdited = Date.now();
    this.keyring.addFromJson(json);
    this.accounts.add(json.address, json);
  }

  forgetAccount(address) {
    this.keyring.removePair(address);
    this.accounts.remove(address);
  }

  forgetAddress(address) {
    this.addresses.remove(address);
  }

  getAccount(address) {
    return this.getAddress(address, 'account');
  }

  getAccounts() {
    const available = this.accounts.subject.getValue();
    return Object.keys(available).map(address => this.getAddress(address, 'account')).filter(account => _development.default.isDevelopment() || account.getMeta().isTesting !== true);
  }

  getAddress(_address) {
    let type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'address';

    const _address2 = (0, _util.isString)(_address) ? _address : this.encodeAddress(_address);

    const _publicKey = this.decodeAddress(_address2);

    const subject = type === 'account' ? this.accounts.subject : this.addresses.subject;
    return {
      address: () => _address2,
      isValid: () => !!subject.getValue()[_address2],
      publicKey: () => _publicKey,
      getMeta: () => subject.getValue()[_address2].json.meta
    };
  }

  getAddresses() {
    const available = this.addresses.subject.getValue();
    return Object.keys(available).map(address => this.getAddress(address));
  }

  rewriteKey(json, key, hexAddr, creator) {
    if (hexAddr.substr(0, 2) === '0x') {
      return;
    }

    _store.default.remove(key);

    _store.default.set(creator(hexAddr), json);
  }

  loadAccount(json, key) {
    if (!json.meta.isTesting && json.encoded) {
      // FIXME Just for the transition period (ignoreChecksum)
      const pair = this.keyring.addFromJson(json, true);
      this.accounts.add(pair.address(), json);
    }

    const _key$split = key.split(':'),
          _key$split2 = (0, _slicedToArray2.default)(_key$split, 2),
          hexAddr = _key$split2[1];

    this.rewriteKey(json, key, hexAddr.trim(), _defaults.accountKey);
  }

  loadAddress(json, key) {
    const _json$meta = json.meta,
          isRecent = _json$meta.isRecent,
          _json$meta$whenCreate = _json$meta.whenCreated,
          whenCreated = _json$meta$whenCreate === void 0 ? 0 : _json$meta$whenCreate;

    if (isRecent && Date.now() - whenCreated > RECENT_EXPIRY) {
      _store.default.remove(key);

      return;
    }

    const address = this.encodeAddress((0, _util.isHex)(json.address) ? (0, _util.hexToU8a)(json.address) // FIXME Just for the transition period (ignoreChecksum)
    : this.decodeAddress(json.address, true));

    const _key$split3 = key.split(':'),
          _key$split4 = (0, _slicedToArray2.default)(_key$split3, 2),
          hexAddr = _key$split4[1];

    this.addresses.add(address, json);
    this.rewriteKey(json, key, hexAddr, _defaults.addressKey);
  }

  loadAll(options) {
    super.initKeyring(options);

    _store.default.each((json, key) => {
      if (_defaults.accountRegex.test(key)) {
        this.loadAccount(json, key);
      } else if (_defaults.addressRegex.test(key)) {
        this.loadAddress(json, key);
      }
    });

    _options.default.init(this);
  }

  restoreAccount(json, password) {
    const type = Array.isArray(json.encoding.content) ? json.encoding.content[1] : 'ed25519';
    const pair = (0, _pair.default)(type, {
      // FIXME Just for the transition period (ignoreChecksum)
      publicKey: this.decodeAddress(json.address, true)
    }, json.meta, (0, _util.hexToU8a)(json.encoded)); // unlock, save account and then lock (locking cleans secretKey, so needs to be last)

    pair.decodePkcs8(password);
    this.addPair(pair, password);
    pair.lock();
    return pair;
  }

  saveAccount(pair, password) {
    this.addTimestamp(pair);
    const json = pair.toJson(password);
    this.keyring.addFromJson(json);
    this.accounts.add(json.address, json);
    return json;
  }

  saveAccountMeta(pair, meta) {
    const address = pair.address();

    const json = _store.default.get((0, _defaults.accountKey)(address));

    pair.setMeta(meta);
    json.meta = pair.getMeta();
    this.accounts.add(json.address, json);
  }

  saveAddress(address, meta) {
    const available = this.addresses.subject.getValue();
    const json = available[address] && available[address].json || {
      address,
      meta: {
        isRecent: void 0,
        whenCreated: Date.now()
      }
    };
    Object.keys(meta).forEach(key => {
      json.meta[key] = meta[key];
    });
    delete json.meta.isRecent;
    this.addresses.add(address, json);
    return json;
  }

  saveRecent(address) {
    const available = this.addresses.subject.getValue();

    if (!available[address]) {
      const json = {
        address,
        meta: {
          isRecent: true,
          whenCreated: Date.now()
        }
      };
      this.addresses.add(address, json);
    }

    return this.addresses.subject.getValue()[address];
  }

}

exports.Keyring = Keyring;
const keyringInstance = new Keyring();
var _default = keyringInstance;
exports.default = _default;